import type { LabReading, StatusIndicator } from "../types";
import { LabStatusBadge } from "../atoms/LabStatusBadge";
import { LabRangeBar } from "../atoms/LabRangeBar";
import { ReferenceRange } from "../atoms/ReferenceRange";
import { parseRefRangeToNumbers } from "../utils";
import { cn } from "../../../lib/utils";
import { useDateFnsFormattersWithLocale } from "@tellescope/react-hooks";

interface LabRangeBarSectionProps {
  refRange: string;
  value: number;
  indicator?: StatusIndicator;
}

const LabRangeBarSection = ({ refRange, value, indicator }: LabRangeBarSectionProps) => {
  const refRangeParsed = parseRefRangeToNumbers(refRange);
  if (!refRangeParsed) return null;
  return (
    <div className="mt-3">
      <LabRangeBar
        value={value}
        refRangeMin={refRangeParsed.refRangeMin}
        refRangeMax={refRangeParsed.refRangeMax}
        indicator={indicator}
        className="h-2"
      />
    </div>
  );
};

export interface LabGraphHeaderProps extends React.ComponentProps<"div"> {
  reading: LabReading;
}

export const LabGraphHeader = ({ reading, className, ...props }: LabGraphHeaderProps) => {
  const { format } = useDateFnsFormattersWithLocale();

  return (
    <div
      data-slot="lab-graph-header"
      className={cn("px-5 py-4 border-b border-gray-100 dark:border-gray-800", className)}
      {...props}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">
            {format(new Date(reading.date), "MMMM d, yyyy")}
          </p>
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-4xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">{reading.value}</span>
            <span className="text-base text-gray-500 dark:text-gray-400">{reading.unit}</span>
          </div>
          {reading.refRange && <ReferenceRange range={reading.refRange} unit={reading.unit} />}
        </div>
        <LabStatusBadge indicator={reading.statusIndicator} />
      </div>

      {reading.refRange && (
        <LabRangeBarSection refRange={reading.refRange} value={reading.value} indicator={reading.statusIndicator} />
      )}
    </div>
  );
};
